/**
 * Step01 안전보건정보 — 기타 안전·보건상 정보 모달 (SafetyInfo 초록 톤 UI)
 */
import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio,
  Checkbox,
} from '@mui/material';
import { Dialog } from '../../../../../../Components/MUIs';

const ACCENT = '#2E7D32';

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    bgcolor: '#FAFCFA',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover fieldset': { borderColor: 'rgba(46, 125, 50, 0.35)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '1px' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(46, 125, 50, 0.12)',
    },
  },
};

const RADIO_PROPS = {
  size: 'small',
  sx: { color: ACCENT, '&.Mui-checked': { color: ACCENT } },
};

const CHECKBOX_SX = { color: ACCENT, '&.Mui-checked': { color: ACCENT } };

const LABEL_SX = { '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#37474F' } };

function EtcSection({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 2,
        border: '1px solid rgba(46, 125, 50, 0.14)',
        bgcolor: '#fff',
        boxShadow: '0 2px 16px rgba(16, 40, 24, 0.07)',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          fontWeight: 800,
          color: '#153019',
          letterSpacing: '-0.02em',
          mb: 2,
          pb: 1.25,
          borderBottom: '1px solid rgba(0,0,0,0.07)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 4,
            height: 20,
            borderRadius: 10,
            bgcolor: ACCENT,
            flexShrink: 0,
          }}
        />
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/**
 * EtcInfoModal Component
 * Modal for editing additional safety and health information (기타 안전·보건상 정보)
 *
 * Collects various safety-related information including:
 * - Accident history (3 years, near-miss incidents)
 * - Worker demographics
 * - Shift work information
 * - Heavy lifting operations
 * - Transport means
 * - Safety work permit requirement
 * - Work environment measurement
 * - Special safety training requirement
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSave - Save handler (confirmation)
 * @param {Object} props.form - Form data object (from useSafetyForm hook)
 * @param {Function} props.onFieldChange - Handler for form field changes
 * @param {Function} props.onTransportMeansToggle - Handler for transport means toggle
 * @returns {JSX.Element} EtcInfoModal component
 */
const EtcInfoModal = ({
  open,
  onClose,
  onSave,
  form = {},
  onFieldChange,
  onTransportMeansToggle,
}) => {
  /**
   * Transport means options
   */
  const transportMeansOptions = ['기계', '인력'];

  return (
    <Dialog
      title="기타 안전·보건상 정보"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      saveButtonName="확인"
      closeButtonName="취소"
      useSaveButton={true}
      onSave={onSave}
      content={
        <Stack
          spacing={2.5}
          sx={{
            p: 2,
            bgcolor: '#EDF3ED',
            maxHeight: 'min(70vh, 620px)',
            overflow: 'auto',
            borderRadius: 1,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(46, 125, 50, 0.25)',
              borderRadius: 4,
            },
          }}
        >
          <EtcSection title="3년간 재해 및 아차사고">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box
                sx={{
                  flex: 1,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: 'rgba(232, 245, 233, 0.65)',
                  border: '1px solid rgba(46, 125, 50, 0.18)',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E4A32', display: 'block', mb: 1 }}>
                  3년간 재해발생 건수
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={form.accidentCnt || ''}
                    onChange={(e) => onFieldChange('accidentCnt', e.target.value)}
                    placeholder="0"
                    sx={{ width: 88, ...FIELD_SX }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                  <Typography variant="body2" sx={{ color: '#546E7A', fontWeight: 600 }}>
                    건
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: 'rgba(232, 245, 233, 0.65)',
                  border: '1px solid rgba(46, 125, 50, 0.18)',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E4A32', display: 'block', mb: 1 }}>
                  아차사고 사례 및 발굴 건수
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={form.nearMissCnt || ''}
                    onChange={(e) => onFieldChange('nearMissCnt', e.target.value)}
                    placeholder="0"
                    sx={{ width: 88, ...FIELD_SX }}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                  <Typography variant="body2" sx={{ color: '#546E7A', fontWeight: 600 }}>
                    건
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </EtcSection>

          <EtcSection title="근로자 구성 및 경력 특성">
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#FAFCFA',
                border: '1px dashed rgba(46, 125, 50, 0.2)',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.femaleWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('femaleWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('여성근로자')) {
                          currentTrait.push('여성근로자');
                        }
                      } else {
                        const index = currentTrait.indexOf('여성근로자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="여성 근로자"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.under1YearWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('under1YearWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('1년미만미숙련자')) {
                          currentTrait.push('1년미만미숙련자');
                        }
                      } else {
                        const index = currentTrait.indexOf('1년미만미숙련자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="1년 미만 미숙련자"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.elderlyWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('elderlyWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('고령근로자')) {
                          currentTrait.push('고령근로자');
                        }
                      } else {
                        const index = currentTrait.indexOf('고령근로자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="고령 근로자"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.irregularWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('irregularWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('비정규직근로자')) {
                          currentTrait.push('비정규직근로자');
                        }
                      } else {
                        const index = currentTrait.indexOf('비정규직근로자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="비정규직 근로자"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.foreignWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('foreignWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('외국인근로자')) {
                          currentTrait.push('외국인근로자');
                        }
                      } else {
                        const index = currentTrait.indexOf('외국인근로자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="외국인 근로자"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={CHECKBOX_SX}
                    size="small"
                    checked={form.disabledWorker || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onFieldChange('disabledWorker', checked);
                      // WorkerTrait 업데이트
                      const currentTrait = (form.workerTrait || '').split(',').filter(Boolean);
                      if (checked) {
                        if (!currentTrait.includes('장애근로자')) {
                          currentTrait.push('장애근로자');
                        }
                      } else {
                        const index = currentTrait.indexOf('장애근로자');
                        if (index > -1) {
                          currentTrait.splice(index, 1);
                        }
                      }
                      onFieldChange('workerTrait', currentTrait.join(','));
                    }}
                  />
                }
                label="장애 근로자"
              />
            </Box>
          </EtcSection>

          <EtcSection title="교대작업 유무">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <RadioGroup
                row
                value={
                  form.shiftType === 'Y'
                    ? '유'
                    : form.shiftType === 'N'
                      ? '무'
                      : ''
                }
                onChange={(e) => {
                  const value = e.target.value === '유' ? 'Y' : 'N';
                  onFieldChange('shiftType', value);
                }}
              >
                <FormControlLabel
                  value="유"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="유"
                  sx={LABEL_SX}
                />
                <FormControlLabel
                  value="무"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="무"
                  sx={LABEL_SX}
                />
              </RadioGroup>
              {form.shiftType === 'Y' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    value={form.shiftCount || ''}
                    onChange={(e) =>
                      onFieldChange('shiftCount', e.target.value)
                    }
                    placeholder="교대 수"
                    sx={{ width: 96, ...FIELD_SX }}
                  />
                  <Typography variant="body2" sx={{ color: '#546E7A', fontWeight: 600 }}>
                    교대
                  </Typography>
                </Box>
              )}
            </Box>
          </EtcSection>

          <EtcSection title="중량물 인력취급시 단위중량(kg) 및 취급형태">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="number"
                  size="small"
                  value={form.handlingWeight || ''}
                  onChange={(e) =>
                    onFieldChange('handlingWeight', e.target.value)
                  }
                  placeholder="0"
                  sx={{ width: 88, ...FIELD_SX }}
                  inputProps={{
                    style: { textAlign: 'right' },
                  }}
                />
                <Typography variant="body2" sx={{ color: '#546E7A', fontWeight: 600 }}>
                  kg
                </Typography>
              </Box>
              <RadioGroup
                row
                value={form.handlingForm || ''}
                onChange={(e) => onFieldChange('handlingForm', e.target.value)}
              >
                <FormControlLabel
                  value="들기"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="들기"
                  sx={LABEL_SX}
                />
                <FormControlLabel
                  value="밀기"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="밀기"
                  sx={LABEL_SX}
                />
                <FormControlLabel
                  value="끌기"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="끌기"
                  sx={LABEL_SX}
                />
              </RadioGroup>
            </Box>
          </EtcSection>

          <EtcSection title="운반수단">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#FAFCFA',
                border: '1px solid rgba(46, 125, 50, 0.1)',
              }}
            >
              <RadioGroup
                row
                value={form.transportMethod || ''}
                onChange={(e) =>
                  onFieldChange('transportMethod', e.target.value)
                }
              >
                {transportMeansOptions.map((means) => (
                  <Box
                    key={means}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <FormControlLabel
                      value={means}
                      control={<MuiRadio {...RADIO_PROPS} />}
                      label={means}
                      sx={LABEL_SX}
                    />
                    {form.transportMethod === means && (
                      <TextField
                        size="small"
                        value={form[`transportMethod${means}`] || ''}
                        onChange={(e) =>
                          onFieldChange(
                            `transportMethod${means}`,
                            e.target.value
                          )
                        }
                        placeholder={`${means} 상세 입력`}
                        sx={{ minWidth: 200, flex: 1, ...FIELD_SX }}
                      />
                    )}
                  </Box>
                ))}
              </RadioGroup>
            </Box>
          </EtcSection>

          <EtcSection title="안전작업허가증 필요작업 유무">
            <RadioGroup
              row
              value={
                form.isSafetyWorkPermit === 'Y'
                  ? '유'
                  : form.isSafetyWorkPermit === 'N'
                    ? '무'
                    : ''
              }
              onChange={(e) => {
                const value = e.target.value === '유' ? 'Y' : 'N';
                onFieldChange('isSafetyWorkPermit', value);
              }}
            >
              <FormControlLabel
                value="유"
                control={<MuiRadio {...RADIO_PROPS} />}
                label="유"
                sx={LABEL_SX}
              />
              <FormControlLabel
                value="무"
                control={<MuiRadio {...RADIO_PROPS} />}
                label="무"
                sx={LABEL_SX}
              />
            </RadioGroup>
          </EtcSection>

          <EtcSection title="작업환경측정 측정유무">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <RadioGroup
                row
                value={
                  form.isEnvironmentMeasurement === '0'
                    ? '측정'
                    : form.isEnvironmentMeasurement === '1'
                      ? '미측정'
                      : form.isEnvironmentMeasurement === '2'
                        ? '해당무'
                        : ''
                }
                onChange={(e) => {
                  const valueMap = { 측정: '0', 미측정: '1', 해당무: '2' };
                  onFieldChange(
                    'isEnvironmentMeasurement',
                    valueMap[e.target.value]
                  );
                }}
              >
                <FormControlLabel
                  value="측정"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="측정"
                  sx={LABEL_SX}
                />
                <FormControlLabel
                  value="미측정"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="미측정"
                  sx={LABEL_SX}
                />
                <FormControlLabel
                  value="해당무"
                  control={<MuiRadio {...RADIO_PROPS} />}
                  label="해당무"
                  sx={LABEL_SX}
                />
              </RadioGroup>
              {form.isEnvironmentMeasurement === '0' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    ml: { xs: 0, sm: 1 },
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: 'rgba(232, 245, 233, 0.5)',
                    border: '1px solid rgba(46, 125, 50, 0.15)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E4A32' }}>
                    측정값
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={form.environmentMeasurementValue || ''}
                    onChange={(e) =>
                      onFieldChange(
                        'environmentMeasurementValue',
                        e.target.value
                      )
                    }
                    placeholder="0"
                    sx={{ width: 104, ...FIELD_SX }}
                    inputProps={{
                      style: { textAlign: 'right' },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#546E7A' }}>
                    ppm
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#78909C' }}>
                    (노출기준
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={form.environmentMeasurementStandard || ''}
                    onChange={(e) =>
                      onFieldChange(
                        'environmentMeasurementStandard',
                        e.target.value
                      )
                    }
                    placeholder="0"
                    sx={{ width: 104, ...FIELD_SX }}
                    inputProps={{
                      style: { textAlign: 'right' },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#546E7A' }}>
                    ppm)
                  </Typography>
                </Box>
              )}
            </Box>
          </EtcSection>

          <EtcSection title="작업에 대한 특별안전교육 필요유무">
            <RadioGroup
              row
              value={
                form.isSpecialSafetyTraining === 'Y'
                  ? '유'
                  : form.isSpecialSafetyTraining === 'N'
                    ? '무'
                    : ''
              }
              onChange={(e) => {
                const value = e.target.value === '유' ? 'Y' : 'N';
                onFieldChange('isSpecialSafetyTraining', value);
              }}
            >
              <FormControlLabel
                value="유"
                control={<MuiRadio {...RADIO_PROPS} />}
                label="유"
                sx={LABEL_SX}
              />
              <FormControlLabel
                value="무"
                control={<MuiRadio {...RADIO_PROPS} />}
                label="무"
                sx={LABEL_SX}
              />
            </RadioGroup>
          </EtcSection>
        </Stack>
      }
    />
  );
};

export default EtcInfoModal;
